import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { AttestationGate } from './AttestationGate';
import { useAuth } from './useAuth';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Path to redirect unauthenticated users to. Defaults to `/login`. */
  redirectTo?: string;
}

/**
 * Renders `children` only if the user is signed in. Otherwise redirects to the
 * login route, preserving the originally requested location in router state so
 * the login flow can return the user to where they were headed.
 *
 * Signed-in users must also complete the per-login attestation (PLA-1110)
 * before `children` render; see AttestationGate.
 */
export function ProtectedRoute({ children, redirectTo = '/login' }: ProtectedRouteProps) {
  const { isAuthenticated, isInteractionInProgress } = useAuth();
  const location = useLocation();

  if (isInteractionInProgress) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  return <AttestationGate>{children}</AttestationGate>;
}
